(() => {
  const initVideoWrapper = (wrapper) => {
    if (wrapper.dataset.videoInitialised) return;
    wrapper.dataset.videoInitialised = 'true';

    const video = wrapper.querySelector('video');
    const fallback = wrapper.querySelector('.slideshow-video-fallback');
    const playBtn = wrapper.querySelector('.video-play-toggle');
    const soundBtn = wrapper.querySelector('.video-sound-toggle');
    const iconBtn = wrapper.querySelector('.slideshow-video-play-pause');

    if (!video) return;

    const desktopSrc =
      wrapper.dataset.desktopVideoSrc ||
      (video.querySelector('source') && video.querySelector('source').getAttribute('src')) ||
      null;
    const mobileSrc = wrapper.dataset.mobileVideoSrc || null;

    // A section can widen what counts as mobile — a phone held in landscape is
    // wider than the theme breakpoint yet still wants the mobile file. Sections
    // that set nothing keep the breakpoint.
    const mobileQuery = wrapper.dataset.mobileVideoQuery || '(max-width: 749px)';

    const setVideoSource = () => {
      if (!desktopSrc) return;
      const isMobile = window.matchMedia(mobileQuery).matches;
      const chosen = isMobile && mobileSrc ? mobileSrc : desktopSrc;
      const srcEl = video.querySelector('source');
      if (srcEl && srcEl.getAttribute('src') !== chosen) {
        const wasPlaying = !video.paused && !video.ended;
        srcEl.setAttribute('src', chosen);
        try {
          video.load();
          if (wasPlaying) video.play();
          // eslint-disable-next-line no-unused-vars
        } catch (e) {
          // ignore load/play errors
        }
      }
    };

    const tryAutoplay = () => {
      if (!video.autoplay) return;
      video.load();
      video.play().catch(() => {
        // autoplay blocked by browser
      });
    };

    setVideoSource();
    tryAutoplay();

    try {
      const mq = window.matchMedia(mobileQuery);
      if (mq.addEventListener) mq.addEventListener('change', setVideoSource);
      else if (mq.addListener) mq.addListener(setVideoSource);
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      // ignore matchMedia errors
    }

    if (fallback) {
      const showFallback = () => {
        video.style.display = 'none';
        fallback.style.zIndex = '1';
      };

      // A stall is ordinary buffering, not a failure. Hiding the video for one
      // leaves it playing unseen — audible, with the poster sitting over a
      // picture that is still running underneath.
      const hideIfFailed = () => {
        if (video.error || video.readyState === 0) showFallback();
      };

      const restore = () => {
        video.style.display = '';
        fallback.style.zIndex = '';
      };

      video.addEventListener('error', showFallback);
      video.addEventListener('stalled', hideIfFailed);
      video.addEventListener('playing', restore);
    }

    if (playBtn) {
      playBtn.addEventListener('click', () => {
        if (video.paused) {
          video.play();
          playBtn.textContent = 'Pause';
        } else {
          video.pause();
          playBtn.textContent = 'Play';
        }
      });
    }

    if (soundBtn) {
      soundBtn.addEventListener('click', () => {
        video.muted = !video.muted;
        soundBtn.textContent = video.muted ? 'Sound On' : 'Sound Off';
      });
    }

    if (iconBtn) {
      const iconPlay = iconBtn.querySelector('.icon-play');
      const iconPause = iconBtn.querySelector('.icon-pause');

      const syncIcons = () => {
        const paused = video.paused;
        if (iconPlay) iconPlay.style.display = paused ? '' : 'none';
        if (iconPause) iconPause.style.display = paused ? 'none' : '';
        iconBtn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
        iconBtn.dataset.linkText = paused ? 'videoPlay' : 'videoPause';
      };

      iconBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (video.paused) {
          video.style.display = '';
          if (fallback) fallback.style.zIndex = '';
        }
        // Recorded so returning to this slide resumes only what the viewer left
        // playing, rather than overriding a deliberate pause.
        wrapper.dataset.videoUserState = video.paused ? 'playing' : 'paused';
        video.paused ? video.play() : video.pause();
      });

      video.addEventListener('play', syncIcons);
      video.addEventListener('pause', syncIcons);
      syncIcons();
    }

    const muteBtn = wrapper.querySelector('.slideshow-video-mute-unmute');

    if (muteBtn) {
      muteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        video.muted = !video.muted;
        muteBtn.querySelector('.icon-unmute').style.display = video.muted ? 'none' : '';
        muteBtn.querySelector('.icon-mute').style.display = video.muted ? '' : 'none';
        muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      });

      muteBtn.querySelector('.icon-unmute').style.display = video.muted ? 'none' : '';
      muteBtn.querySelector('.icon-mute').style.display = video.muted ? '' : 'none';
    }
  };

  // Scope defaults to the document. Sections that insert video markup after this
  // first pass — the personalized slideshow swapping in an experience — re-run it
  // against their own subtree; the data-video-initialised guard keeps wrappers
  // that are already bound from picking up a second set of listeners.
  const init = (scope) => {
    (scope || document).querySelectorAll('.slideshow-video-wrapper').forEach(initVideoWrapper);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  const eachVideo = (root, callback) => {
    if (!root) return;

    root.querySelectorAll('.slideshow-video-wrapper').forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      if (video) callback(video, wrapper);
    });
  };

  // Only the slide on screen keeps playing: a video scrolled out of view carries
  // its audio over the slide that replaced it otherwise. An autoplay slide
  // resumes on return unless the viewer paused it by hand.
  const setActiveSlide = (root, currentSlide) => {
    if (!currentSlide) return;

    eachVideo(root, (video, wrapper) => {
      if (!currentSlide.contains(wrapper)) {
        video.pause();
        return;
      }

      const userState = wrapper.dataset.videoUserState;
      const shouldPlay = userState ? userState === 'playing' : video.autoplay;

      // Asked for whatever the element reports, not only when it reports itself
      // paused: an autoplay attempt that never started still leaves paused
      // false, and a play request on one that genuinely is playing is a no-op.
      if (shouldPlay) {
        video.play().catch(() => {
          // blocked by the browser's autoplay policy
        });
      }
    });
  };

  // For a slideshow that is off screen entirely, where no slide should hold
  // either the picture or the audio.
  const pauseWithin = (root) => {
    eachVideo(root, (video) => video.pause());
  };

  window.SlideshowVideo = { init, setActiveSlide, pauseWithin };
})();
